import React, { useState } from "react";
import {
    Elements,
    PaymentElement,
    useStripe,
    useElements,
} from "@stripe/react-stripe-js";
import { loadStripe, StripeElementsOptions } from "@stripe/stripe-js";
import { Button, Spinner, Alert } from "reactstrap";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "");

interface StripeCheckoutProps {
    clientSecret: string;
    amount: number;
    onSuccess: () => void;
    onError: (error: string) => void;
}

interface CheckoutFormProps {
    amount: number;
    onSuccess: () => void;
    onError: (error: string) => void;
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({ amount, onSuccess, onError }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!stripe || !elements || !isReady) {
            return;
        }

        setIsProcessing(true);
        setErrorMessage(null);

        try {
            // Submit the form first to validate
            const { error: submitError } = await elements.submit();
            if (submitError) {
                setErrorMessage(submitError.message || "Validation failed");
                onError(submitError.message || "Validation failed");
                setIsProcessing(false);
                return;
            }

            const { error, paymentIntent } = await stripe.confirmPayment({
                elements,
                confirmParams: {
                    return_url: `${window.location.origin}/admin/profile?tab=upgrades`,
                },
                redirect: "if_required",
            });

            if (error) {
                setErrorMessage(error.message || "An error occurred");
                onError(error.message || "Payment failed");
                setIsProcessing(false);
            } else if (paymentIntent && (paymentIntent.status === "succeeded" || paymentIntent.status === "requires_capture")) {
                // "succeeded" for immediate capture, "requires_capture" for authorization-only payments
                onSuccess();
            } else {
                setIsProcessing(false);
            }
        } catch (err: any) {
            setErrorMessage(err.message || "An unexpected error occurred");
            onError(err.message || "Payment failed");
            setIsProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            {errorMessage && (
                <Alert color="danger" className="mb-3">
                    {errorMessage}
                </Alert>
            )}

            <div className="mb-4">
                <PaymentElement
                    onReady={() => setIsReady(true)}
                    onLoadError={(error) => {
                        setErrorMessage(error.error.message || "Failed to load payment form");
                        onError(error.error.message || "Failed to load payment form");
                    }}
                />
            </div>

            {!isReady && (
                <div className="text-center mb-3">
                    <Spinner size="sm" color="primary" />
                    <small className="text-muted ms-2">Loading payment form...</small>
                </div>
            )}

            <Button
                color="success"
                block
                type="submit"
                disabled={!stripe || !isReady || isProcessing}
            >
                {isProcessing ? (
                    <>
                        <Spinner size="sm" className="me-2" />
                        Processing...
                    </>
                ) : (
                    `Pay $${amount.toFixed(2)}`
                )}
            </Button>
        </form>
    );
};

const StripeCheckout: React.FC<StripeCheckoutProps> = ({
    clientSecret,
    amount,
    onSuccess,
    onError,
}) => {
    const options: StripeElementsOptions = {
        clientSecret,
        appearance: {
            theme: "stripe",
            variables: {
                colorPrimary: "#5e72e4",
            },
        },
    };

    return (
        <Elements stripe={stripePromise} options={options}>
            <CheckoutForm amount={amount} onSuccess={onSuccess} onError={onError} />
        </Elements>
    );
};

export default StripeCheckout;
